import { createApp } from 'vue';
import { store } from '../store/index';

const App = {
    data: {
        exemple: 'data example test success',
    },
    components: {},
    vueObjects: {},
    componentsVue: {},
    store,
    debug: false,
    mode: process.env.NODE_ENV,

    /**
     * Extract props from DOM element attributes
     */
    extractPropsFromElement(element, component) {
        const props = {};
        const componentProps = component.props || [];

        // Handle different prop definition formats
        let propNames = [];
        if (Array.isArray(componentProps)) {
            propNames = componentProps;
        } else if (typeof componentProps === 'object') {
            propNames = Object.keys(componentProps);
        }

        // Extract attributes that match component props
        for (const propName of propNames) {
            // Check for kebab-case and camelCase variants
            const kebabCase = propName.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`);

            if (element.hasAttribute(propName)) {
                props[propName] = this.parseAttributeValue(element.getAttribute(propName));
            } else if (element.hasAttribute(kebabCase)) {
                props[propName] = this.parseAttributeValue(element.getAttribute(kebabCase));
            } else if (element.hasAttribute(`:${propName}`)) {
                // Vue binding syntax
                const value = element.getAttribute(`:${propName}`);
                props[propName] = this.evaluateExpression(value);
            } else if (element.hasAttribute(`:${kebabCase}`)) {
                // Vue binding syntax with kebab-case
                const value = element.getAttribute(`:${kebabCase}`);
                props[propName] = this.evaluateExpression(value);
            }
        }

        // Also check for data attributes
        for (const attr of element.attributes) {
            if (attr.name.startsWith('data-')) {
                const propName = attr.name.replace('data-', '').replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                if (propNames.includes(propName)) {
                    props[propName] = this.parseAttributeValue(attr.value);
                }
            }
        }

        return props;
    },

    /**
     * Parse attribute value (try to detect JSON, numbers, booleans)
     */
    parseAttributeValue(value) {
        if (value === 'true') return true;
        if (value === 'false') return false;
        if (value === 'null') return null;
        if (value === 'undefined') return undefined;

        // Try to parse as number
        if (!isNaN(value) && !isNaN(parseFloat(value))) {
            return parseFloat(value);
        }

        // Try to parse as JSON
        if ((value.startsWith('{') && value.endsWith('}')) ||
            (value.startsWith('[') && value.endsWith(']'))) {
            try {
                return JSON.parse(value);
            } catch (e) {
                // If JSON parsing fails, return as string
            }
        }

        return value;
    },

    /**
     * Evaluate Vue expression (basic implementation)
     */
    evaluateExpression(expression) {
        try {
            if (expression.startsWith('{') || expression.startsWith('[')) {
                return JSON.parse(expression);
            }
            return Function('"use strict"; return (' + expression + ')')();
        } catch (e) {
            console.warn('Failed to evaluate expression:', expression, e);
            return expression;
        }
    },

    init() {
        const fingerKey = 'dbKey';

        for (const code in this.components) {
            const curComponent = this.components[code];
            const selector = curComponent.selector;

            if (!selector) continue;

            const elements = document.querySelectorAll(selector);
            if (elements.length > 1) {
                elements.forEach((el, index) => {
                    el.classList.add(`${fingerKey}${index}`);

                    // Extract props from DOM element
                    const props = this.extractPropsFromElement(el, curComponent);
                    const originalContent = el.innerHTML;
                    el.innerHTML = ''; // Clear original content

                    // Create wrapper component that passes props
                    const app = createApp({
                        components: {
                            [code]: curComponent
                        },
                        data() {
                            return {
                                componentProps: props
                            };
                        },
                        template: `<${code} v-bind="componentProps">${originalContent}</${code}>`
                    });

                    app.use(store);
                    const _app = app.mount(`${selector}.${fingerKey}${index}`);

                    if (this.debug) {
                        console.log(`Vue component mounted: ${code}[${index}] with props:`, props);
                    }
                });
            } else if (elements.length === 1) {
                const el = elements[0];
                const originalContent = el.innerHTML;
                el.innerHTML = ''; // Clear original content

                // Extract props from DOM element
                const props = this.extractPropsFromElement(el, curComponent);

                // Create wrapper component that passes props
                const app = createApp({
                    components: {
                        [code]: curComponent
                    },
                    data() {
                        return {
                            componentProps: props
                        };
                    },
                    template: `<${code} v-bind="componentProps">${originalContent}</${code}>`
                });

                app.use(store);
                this.componentsVue[code] = app.mount(selector);
                //console.log('this.componentsVue[code]', code, this.componentsVue[code])

                if (curComponent.globalName) {
                    window[curComponent.globalName] = this.componentsVue[code];
                }

                if (this.debug) {
                    console.log(`Vue component mounted: ${code} with props:`, props);
                }
            }
        }

        for (const code in this.vueObjects) {
            const obj = this.vueObjects[code];
            const el = document.querySelector(obj.el);
            if (obj.globalName && el) {
                // For vueObjects, extract props if the object has props defined
                let props = {};
                if (obj.props) {
                    props = this.extractPropsFromElement(el, obj);
                }

                // Create app directly from the object configuration
                const app = createApp({
                    ...obj,
                    data() {
                        return {
                            ...obj.data?.() || {},
                            ...props
                        };
                    }
                });

                app.use(store);
                this.componentsVue[code] = app.mount(obj.el);
                window[obj.globalName] = this.componentsVue[code];
            }
        }

        if (this.debug) {
            console.log('Global App object:', this);
        }

        this.handlerWindowResize();
    },

    setComponent(vueComponent) {
        if (!vueComponent?.name) {
            console.warn('Failed to set vue component', vueComponent);
        } else {
            this.components[vueComponent.name] = vueComponent;
        }
    },

    setVyeObject(vueObject) {
        if (!vueObject?.name) {
            console.warn('Failed to set vue object', vueObject);
        } else {
            this.vueObjects[vueObject.name] = vueObject;
        }
    },

    getComponent(name) {
        return this.components[name];
    },

    getVueComponent(componentName) {
        return this.componentsVue[componentName] ?? console.warn(`Component not found: ${componentName}`);
    },

    empty(value) {
        if (value == null) return true;
        if (typeof value === 'object') return Object.keys(value).length === 0;
        if (typeof value === 'string') return value.trim() === '';
        if (typeof value === 'number') return isNaN(value);
        return false;
    },

    setStoreModules(values) {
        const parsed = typeof values === 'string' ? JSON.parse(values) : values;
        for (const key in parsed) {
            this.setStoreModule(key, parsed[key]);
        }
    },

    getStoreModule(name) {
        return store.state[name]?.data;
    },

    setStores(values) {
        const parsed = typeof values === 'string' ? JSON.parse(values) : values;
        for (const key in parsed) {
            this.setStore(key, parsed[key]);
        }
    },

    getStores() {
        return store.state.results;
    },

    setStore(name, value) {
        store.commit('setStore', { name, value });
        return this.getStore(name);
    },

    setStoreModule(name, value) {
        store.commit('setStoreModule', { name, value });
    },

    getStore(name) {
        return name ? store.state.results[name] : store.state.results;
    },

    handlerWindowResize() {
        const commitWidth = () => store.commit('global/changeScreenWidth');
        let debounced = null;
        let fallbackTimer = null;

        window.addEventListener('resize', () => {
            if (typeof window._?.debounce === 'function') {
                if (!debounced) {
                    debounced = window._.debounce(commitWidth, 300);
                }
                debounced();
            } else {
                clearTimeout(fallbackTimer);
                fallbackTimer = setTimeout(commitWidth, 300);
            }
        });

        if (document.readyState === 'complete') {
            commitWidth();
        } else {
            window.addEventListener('load', commitWidth);
        }
    }
};
export default App;